import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { api, queryString } from './api';
import { CloseIcon, SearchIcon } from './icons';

export interface OptionItem {
  id: string | number;
  label?: string;
  [key: string]: unknown;
}

export function AsyncSelect({
  label,
  path,
  value,
  selectedLabel,
  placeholder = 'Tìm kiếm…',
  disabled,
  error,
  minimumQueryLength = 0,
  pageSize = 30,
  onChange,
  renderOption
}: {
  label: string;
  path: string;
  value?: string | number;
  selectedLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  minimumQueryLength?: number;
  pageSize?: number;
  onChange: (item: OptionItem | null) => void;
  renderOption?: (item: OptionItem) => ReactNode;
}) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const normalizedQuery = query.trim();
  const queryReady = normalizedQuery.length >= minimumQueryLength;

  useEffect(() => {
    if (!open || disabled) return;
    if (!queryReady) {
      setItems([]);
      setHasMore(false);
      setLoading(false);
      setLoadFailed(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const separator = path.includes('?') ? '&' : '?';
        const result = await api<{ items: OptionItem[]; hasMore?: boolean }>(
          `${path}${separator}${queryString({ q: normalizedQuery, limit: pageSize, page })}`,
          {},
          controller.signal
        );
        setItems((current) => page === 0 ? result.items : [...current, ...result.items]);
        setHasMore(Boolean(result.hasMore));
      } catch (error) {
        if (!controller.signal.aborted && page === 0) setItems([]);
        if (!controller.signal.aborted) setLoadFailed(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, page === 0 && normalizedQuery ? 220 : 0);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [disabled, normalizedQuery, open, page, pageSize, path, queryReady, retryToken]);

  useEffect(() => {
    setQuery('');
    setItems([]);
    setHasMore(false);
    setPage(0);
    setLoadFailed(false);
  }, [path]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className={`field async-select ${error ? 'has-error' : ''}`} ref={root}>
      <label htmlFor={id}>{label}</label>
      <div className={`select-control ${open ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}>
        <SearchIcon className="field-icon" />
        <input
          id={id}
          value={open ? query : selectedLabel ?? ''}
          title={!open && selectedLabel ? selectedLabel : undefined}
          placeholder={disabled ? 'Chọn thông tin trước' : placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-options`}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          onFocus={() => {
            setQuery('');
            setItems([]);
            setHasMore(false);
            setPage(0);
            setLoadFailed(false);
            setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setItems([]);
            setHasMore(false);
            setPage(0);
            setLoadFailed(false);
            setOpen(true);
          }}
        />
        {value && !disabled ? (
          <button
            className="clear-select"
            type="button"
            aria-label={`Xóa ${label}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange(null);
              setQuery('');
            }}
          >
            <CloseIcon />
          </button>
        ) : null}
      </div>
      {open && !disabled ? (
        <div className="select-popover" id={`${id}-options`} role="listbox">
          {!queryReady ? <div className="select-message">Nhập ít nhất {minimumQueryLength} ký tự để tìm</div> : null}
          {queryReady && loading && items.length === 0 ? <div className="select-message"><span className="mini-spinner" /> Đang tìm…</div> : null}
          {queryReady && !loading && !loadFailed && items.length === 0 ? <div className="select-message">Không có kết quả phù hợp</div> : null}
          {queryReady
            ? items.map((item) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={String(value) === String(item.id)}
                  className="select-option"
                  key={String(item.id)}
                  onClick={() => {
                    onChange(item);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  {renderOption ? renderOption(item) : item.label}
                </button>
              ))
            : null}
          {queryReady && loading && items.length > 0 ? <div className="select-message"><span className="mini-spinner" /> Đang tải thêm…</div> : null}
          {queryReady && loadFailed && !loading ? <div className="select-message select-message-error">Không thể tải kết quả</div> : null}
          {queryReady && (hasMore || loadFailed) && !loading ? <button type="button" className="select-load-more" onClick={() => loadFailed ? setRetryToken((current) => current + 1) : setPage((current) => current + 1)}>{loadFailed ? 'Thử lại' : 'Tải thêm kết quả'}</button> : null}
        </div>
      ) : null}
      {error ? <span className="field-error" id={`${id}-error`} role="alert">{error}</span> : null}
    </div>
  );
}

export function Toast({ message, tone = 'success', onClose }: { message: string; tone?: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onClose, 4200);
    return () => window.clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${tone}`} role="status">
      <span className="toast-dot" />
      <span>{message}</span>
      <button type="button" aria-label="Đóng thông báo" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

export function LoadingScreen() {
  return (
    <main className="boot-screen">
      <div className="brand-loader"><span /><span /><span /></div>
      <p>Đang tải…</p>
    </main>
  );
}
